import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../store/hooks';
import { logout } from '../../store/slices/authSlice';
import { LOGIN_URL } from '../../utils/api/config';
import { isCompanyAccount, isCompanyUser } from '../../utils/roleUtils';
import {
  UPGRADE_MODAL_VARIANTS,
  useCompanyAccess,
} from '../../context/CompanyAccessContext';
import {
  BILLING_UPGRADE_EVENT,
  TRIAL_NOT_STARTED_EVENT,
  isBillingOnlyAllowedPath,
  TRIAL_EXPIRED_LOGIN_MODAL_KEY,
} from '../../utils/companyBillingGate';
import TrialExpiredUpgradeModal from './TrialExpiredUpgradeModal';

export default function TrialExpiredGate({ children }) {
  const {
    isBillingOnly,
    trialNotStarted,
    trialStartsAt,
    subscriptionExpired,
    loading,
    upgradeModalOpen,
    upgradeModalVariant,
    openUpgradeModal,
    closeUpgradeModal,
  } = useCompanyAccess();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const companyUser = isCompanyUser();
  const canAccessBilling = isCompanyAccount();
  const allowedPath = useMemo(
    () => isBillingOnlyAllowedPath(location.pathname, { canAccessBilling }),
    [location.pathname, canAccessBilling]
  );

  // Login welcome modal — once per session after billing_only handoff (not for scheduled trials)
  useEffect(() => {
    if (loading || !companyUser || !isBillingOnly || trialNotStarted) return;
    if (sessionStorage.getItem(TRIAL_EXPIRED_LOGIN_MODAL_KEY) !== '1') return;

    sessionStorage.removeItem(TRIAL_EXPIRED_LOGIN_MODAL_KEY);
    openUpgradeModal(UPGRADE_MODAL_VARIANTS.LOGIN);
  }, [loading, companyUser, isBillingOnly, trialNotStarted, openUpgradeModal]);

  // Direct URL to a restricted route → send home and explain
  useEffect(() => {
    if (loading || !companyUser || !isBillingOnly || allowedPath) return;
    navigate('/', { replace: true });
    openUpgradeModal(trialNotStarted ? UPGRADE_MODAL_VARIANTS.TRIAL_PENDING : UPGRADE_MODAL_VARIANTS.NAVIGATION);
  }, [loading, companyUser, isBillingOnly, trialNotStarted, allowedPath, navigate, openUpgradeModal, location.pathname]);

  // API 403 billing gate → modal instead of hard redirect
  useEffect(() => {
    const onBillingUpgradeRequired = () => {
      if (!isBillingOnly || trialNotStarted) return;
      openUpgradeModal(UPGRADE_MODAL_VARIANTS.API);
    };

    const onTrialNotStarted = () => {
      if (!isBillingOnly) return;
      openUpgradeModal(UPGRADE_MODAL_VARIANTS.TRIAL_PENDING);
    };

    window.addEventListener(BILLING_UPGRADE_EVENT, onBillingUpgradeRequired);
    window.addEventListener(TRIAL_NOT_STARTED_EVENT, onTrialNotStarted);
    return () => {
      window.removeEventListener(BILLING_UPGRADE_EVENT, onBillingUpgradeRequired);
      window.removeEventListener(TRIAL_NOT_STARTED_EVENT, onTrialNotStarted);
    };
  }, [isBillingOnly, trialNotStarted, openUpgradeModal]);

  const handleUpgrade = () => {
    closeUpgradeModal();
    // Staff cannot open Plan & Billing — modal copy already tells them to ask an admin.
    if (!canAccessBilling) return;
    navigate('/billing?tab=subscription');
  };

  const handleLogout = async () => {
    closeUpgradeModal();
    try {
      await dispatch(logout()).unwrap();
    } catch {
      // still redirect
    }
    window.location.href = LOGIN_URL;
  };

  return (
    <>
      {children}
      <TrialExpiredUpgradeModal
        isOpen={companyUser && isBillingOnly && upgradeModalOpen}
        variant={upgradeModalVariant}
        subscriptionExpired={subscriptionExpired}
        trialNotStarted={trialNotStarted}
        trialStartsAt={trialStartsAt}
        onUpgrade={handleUpgrade}
        onClose={closeUpgradeModal}
        onLogout={handleLogout}
      />
    </>
  );
}
